import { useRef, useState } from "react";
import { Download, FileDown, Upload, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../../lib/api.js";
import { SlateButton } from "../common/UI.jsx";

/**
 * <ImportExportBar entity="plants" label="Plants" onImported={...} />
 *
 * Renders three buttons in the page toolbar:
 *   - Download Template   → GET /foundation/{entity}/template.xlsx
 *   - Download Data       → GET /foundation/{entity}/export.xlsx
 *   - Import              → POST /foundation/{entity}/import (multipart .xlsx)
 *
 * On successful import calls onImported() so the parent page refetches its list.
 * On errors (either "file wrong shape" 400 or per-row cell errors) shows a modal
 * with the full per-row error table.
 */
export function ImportExportBar({ entity, label, onImported, canImport = true, className = "" }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(null); // "template" | "data" | null
    const [errorReport, setErrorReport] = useState(null);

    const download = async (kind /* "template" | "data" */) => {
        setDownloading(kind);
        try {
            const url = kind === "template"
                ? `/foundation/${entity}/template.xlsx`
                : `/foundation/${entity}/export.xlsx`;
            const { data, headers } = await api.get(url, { responseType: "blob" });
            const filename = extractFilename(headers["content-disposition"])
                || (kind === "template" ? `${entity}-template.xlsx` : `${entity}-export.xlsx`);
            triggerDownload(data, filename);
            toast.success(`${kind === "template" ? "Template" : "Data"} downloaded`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDownloading(null);
        }
    };

    const onFilePicked = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Basic client-side gate (backend re-checks). Only accept .xlsx / .xlsm.
        if (!/\.(xlsx|xlsm)$/i.test(file.name)) {
            toast.error("Only .xlsx or .xlsm files are accepted. Please use the template.");
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/foundation/${entity}/import`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
            );
            if (data.processed > 0) {
                toast.success(
                    `Imported ${data.processed} ${label} rows`,
                    { description: `${data.created} created · ${data.updated} updated · ${data.skipped} skipped` },
                );
            }
            if (data.errors && data.errors.length > 0) {
                setErrorReport({ ...data, filename: file.name });
            } else if (data.processed === 0) {
                toast.warning(`No rows imported from ${file.name}. Check the file structure.`);
            }
            onImported?.();
        } catch (err) {
            // Backend returns 400 with detail string on structural failures
            const detail = err?.response?.data?.detail || formatApiError(err);
            toast.error(detail);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <SlateButton
                variant="outline"
                onClick={() => download("template")}
                disabled={!!downloading || uploading}
                className="h-8 text-[11.5px]"
                data-testid={`io-template-${entity}`}
                title="Download a blank Excel template with the required columns"
            >
                {downloading === "template" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                Template
            </SlateButton>
            <SlateButton
                variant="outline"
                onClick={() => download("data")}
                disabled={!!downloading || uploading}
                className="h-8 text-[11.5px]"
                data-testid={`io-export-${entity}`}
                title="Download the current table data as Excel"
            >
                {downloading === "data" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Download
            </SlateButton>
            {canImport && (
                <>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        hidden
                        onChange={onFilePicked}
                        data-testid={`io-file-input-${entity}`}
                    />
                    <SlateButton
                        variant="outline"
                        onClick={() => fileRef.current?.click()}
                        disabled={!!downloading || uploading}
                        className="h-8 text-[11.5px]"
                        data-testid={`io-import-${entity}`}
                        title="Upload an Excel file to add or update rows"
                    >
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploading ? "Uploading…" : "Import"}
                    </SlateButton>
                </>
            )}
            {errorReport && (
                <ImportErrorModal report={errorReport} onClose={() => setErrorReport(null)} />
            )}
        </div>
    );
}


function ImportErrorModal({ report, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={onClose} data-testid="import-error-modal">
            <div className="bg-white rounded-md w-full max-w-3xl max-h-[80vh] flex flex-col shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <div>
                            <div className="font-display font-semibold text-slate-800">Import Report — {report.entity_label}</div>
                            <div className="text-[11.5px] text-slate-500 mt-0.5">
                                {report.filename} · {report.processed} imported · {report.skipped} skipped
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-[12px] px-2 py-1">
                        Close
                    </button>
                </div>
                <div className="p-4 overflow-y-auto">
                    {report.errors.length === 0 ? (
                        <div className="text-[12.5px] text-emerald-700">All rows imported successfully.</div>
                    ) : (
                        <>
                            <div className="text-[12.5px] text-slate-600 mb-3">
                                The following rows were <span className="font-semibold text-red-600">not imported</span>.
                                Fix them in your Excel file and re-upload. Rows without errors were saved.
                            </div>
                            <div className="overflow-x-auto border border-slate-200 rounded-md">
                                <table className="w-full text-[12px]">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="text-left py-2 px-3 eyebrow text-slate-500 w-16">Row</th>
                                            <th className="text-left py-2 px-3 eyebrow text-slate-500 w-48">Column</th>
                                            <th className="text-left py-2 px-3 eyebrow text-slate-500">Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.errors.map((e, i) => (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-1.5 px-3 font-mono">{e.row}</td>
                                                <td className="py-1.5 px-3 font-mono">{e.column}</td>
                                                <td className="py-1.5 px-3 text-red-700">{e.error}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}


// ---- helpers ----
function extractFilename(disposition) {
    if (!disposition) return null;
    const m = /filename="?([^"]+)"?/.exec(disposition);
    return m?.[1] || null;
}

function triggerDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}
